import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Plus, User, Edit, Trash2, Heart, Star } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Layout } from "@/components/Layout";

interface Character {
  id: string;
  name: string;
  type: string;
  personality: string;
  appearance: string;
  age: string;
  favoriteColor: string;
  specialPowers?: string;
  avatar?: string;
  isFavorite: boolean;
  storiesCount: number;
}

const Characters = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [open, setOpen] = useState(false);
  
  // State for form inputs
  const [newCharacter, setNewCharacter] = useState<Omit<Character, "id" | "isFavorite" | "storiesCount">>({
    name: "",
    type: "human",
    personality: "",
    appearance: "",
    age: "child",
    favoriteColor: "blue",
    specialPowers: ""
  });
  
  // Mock data - will be replaced with real data from database
  const [characters, setCharacters] = useState<Character[]>([
    {
      id: "1",
      name: "Luna",
      type: "animal",
      personality: "Brave, curious, and always ready for adventure",
      appearance: "A small white mouse with bright blue eyes and a pink nose",
      age: "child",
      favoriteColor: "purple",
      specialPowers: "Can talk to flowers",
      isFavorite: true,
      storiesCount: 3
    },
    {
      id: "2",
      name: "Max",
      type: "fantasy",
      personality: "Friendly, helpful, and loves to share",
      appearance: "A green dragon with golden scales and kind eyes",
      age: "adult",
      favoriteColor: "green",
      specialPowers: "Can breathe fire (but only for cooking)",
      isFavorite: true,
      storiesCount: 2
    },
    {
      id: "3",
      name: "Bella",
      type: "human",
      personality: "Smart, creative, and loves to solve problems",
      appearance: "A girl with curly brown hair and glasses",
      age: "child",
      favoriteColor: "pink",
      isFavorite: false,
      storiesCount: 1
    }
  ]);

  const characterTypes = [
    { value: "human", label: "Human", emoji: "👤" },
    { value: "animal", label: "Animal", emoji: "🐾" },
    { value: "fantasy", label: "Fantasy", emoji: "🐉" },
    { value: "robot", label: "Robot", emoji: "🤖" },
    { value: "magical", label: "Magical", emoji: "✨" }
  ];

  const ageGroups = [
    { value: "baby", label: "Baby" },
    { value: "child", label: "Child" },
    { value: "teen", label: "Teenager" },
    { value: "adult", label: "Adult" },
    { value: "elder", label: "Elder" }
  ];

  const colors = [
    { value: "red", label: "Red" },
    { value: "blue", label: "Blue" },
    { value: "green", label: "Green" },
    { value: "yellow", label: "Yellow" },
    { value: "purple", label: "Purple" },
    { value: "pink", label: "Pink" },
    { value: "orange", label: "Orange" },
    { value: "brown", label: "Brown" }
  ];

  const filteredCharacters = characters.filter(character => 
    character.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    character.type.toLowerCase().includes(searchQuery.toLowerCase()) ||
    character.personality.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setNewCharacter(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newCharacter.name || !newCharacter.personality) {
      return;
    }
    
    const characterToAdd: Character = {
      ...newCharacter,
      id: Date.now().toString(),
      isFavorite: false,
      storiesCount: 0
    };
    
    setCharacters(prev => [...prev, characterToAdd]);
    
    // Reset form
    setNewCharacter({
      name: "",
      type: "human",
      personality: "",
      appearance: "",
      age: "child",
      favoriteColor: "blue",
      specialPowers: ""
    });
    
    setOpen(false);
  };

  const toggleFavorite = (characterId: string) => {
    setCharacters(prev => 
      prev.map(char => 
        char.id === characterId 
          ? { ...char, isFavorite: !char.isFavorite }
          : char
      )
    );
  };

  const deleteCharacter = (characterId: string) => {
    setCharacters(prev => prev.filter(char => char.id !== characterId));
  };

  const getTypeEmoji = (type: string) => {
    return characterTypes.find(t => t.value === type)?.emoji || "👤";
  };

  const getTypeColor = (type: string) => {
    const colors: { [key: string]: string } = {
      human: "bg-blue-100 text-blue-800",
      animal: "bg-green-100 text-green-800",
      fantasy: "bg-purple-100 text-purple-800",
      robot: "bg-gray-100 text-gray-800",
      magical: "bg-pink-100 text-pink-800"
    };
    return colors[type] || "bg-gray-100 text-gray-800";
  };

  // If still loading, show loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex items-center space-x-2">
          <User className="h-8 w-8 animate-pulse text-purple-500" />
          <span className="text-lg">Loading your characters...</span>
        </div>
      </div>
    );
  }

  // If not logged in, redirect
  if (!user) {
    navigate("/auth");
    return null;
  }

  return (
    <Layout>
      <div className="py-6 px-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold">Characters</h1>
            <p className="text-muted-foreground">
              Create and manage characters for your stories
            </p>
          </div>
          
          <div className="flex w-full md:w-auto gap-2">
            <div className="relative flex-1 md:w-64">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search characters..."
                className="pl-8"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Character
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[600px]">
                <form onSubmit={handleSubmit}>
                  <DialogHeader>
                    <DialogTitle>Create New Character</DialogTitle>
                    <DialogDescription>
                      Design a character that will bring your stories to life.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="grid gap-4 py-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">Character Name *</Label>
                        <Input
                          id="name"
                          name="name"
                          value={newCharacter.name}
                          onChange={handleChange}
                          placeholder="e.g., Luna, Max, Bella"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Character Type</Label>
                        <Select value={newCharacter.type} onValueChange={(value) => setNewCharacter(prev => ({ ...prev, type: value }))}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {characterTypes.map((type) => (
                              <SelectItem key={type.value} value={type.value}>
                                <span className="flex items-center space-x-2">
                                  <span>{type.emoji}</span>
                                  <span>{type.label}</span>
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="personality">Personality *</Label>
                      <Textarea
                        id="personality"
                        name="personality"
                        value={newCharacter.personality}
                        onChange={handleChange}
                        placeholder="Describe their personality, likes, dislikes..."
                        required
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="appearance">Appearance</Label>
                      <Textarea
                        id="appearance"
                        name="appearance"
                        value={newCharacter.appearance}
                        onChange={handleChange}
                        placeholder="Describe how they look..."
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Age</Label>
                        <Select value={newCharacter.age} onValueChange={(value) => setNewCharacter(prev => ({ ...prev, age: value }))}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {ageGroups.map((age) => (
                              <SelectItem key={age.value} value={age.value}>
                                {age.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Favorite Color</Label>
                        <Select value={newCharacter.favoriteColor} onValueChange={(value) => setNewCharacter(prev => ({ ...prev, favoriteColor: value }))}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {colors.map((color) => (
                              <SelectItem key={color.value} value={color.value}>
                                {color.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="specialPowers">Special Powers (Optional)</Label>
                      <Input
                        id="specialPowers"
                        name="specialPowers"
                        value={newCharacter.specialPowers}
                        onChange={handleChange}
                        placeholder="e.g., Can fly, talks to animals, magic powers..."
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit">Create Character</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {filteredCharacters.length === 0 ? (
          <div className="text-center py-12">
            <User className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchQuery ? "No characters found" : "No characters yet"}
            </h3>
            <p className="text-gray-500 mb-6">
              {searchQuery 
                ? "Try adjusting your search terms" 
                : "Create your first character to bring stories to life"
              }
            </p>
            {!searchQuery && (
              <Button onClick={() => setOpen(true)}>
                <Plus className="h-4 w-4 mr-1" />
                Create Your First Character
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCharacters.map((character) => (
              <Card key={character.id} className="hover:shadow-lg transition-shadow">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-2">
                      <span className="text-2xl">{getTypeEmoji(character.type)}</span>
                      <Badge variant="secondary" className={getTypeColor(character.type)}>
                        {character.type}
                      </Badge>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleFavorite(character.id)}
                        className={character.isFavorite ? "text-red-500" : "text-gray-400"}
                      >
                        <Heart className="h-4 w-4" fill={character.isFavorite ? "currentColor" : "none"} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteCharacter(character.id)}
                        className="text-gray-400 hover:text-red-500"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <CardTitle className="text-lg">{character.name}</CardTitle>
                  <CardDescription className="line-clamp-2">
                    {character.personality}
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="space-y-2 text-sm text-gray-600">
                    <div className="flex items-center space-x-2">
                      <span>Age:</span>
                      <span className="capitalize">{character.age}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span>Color:</span>
                      <span className="capitalize">{character.favoriteColor}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Star className="h-4 w-4" />
                      <span>{character.storiesCount} stories</span>
                    </div>
                  </div>
                  
                  {character.appearance && (
                    <div className="mt-3 p-2 bg-gray-50 rounded text-sm">
                      <div className="font-medium text-gray-700 mb-1">Appearance:</div>
                      <div className="text-gray-600">{character.appearance}</div>
                    </div>
                  )}
                  
                  {character.specialPowers && (
                    <div className="mt-3 p-2 bg-purple-50 rounded text-sm">
                      <div className="font-medium text-purple-700 mb-1">Special Powers:</div>
                      <div className="text-purple-600">{character.specialPowers}</div>
                    </div>
                  )}
                  
                  <div className="flex gap-2 mt-4">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="flex-1"
                      onClick={() => navigate("/", { state: { character: character } })}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Use in Story
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="flex-1"
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Characters; 